import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import api, { slugStorefrontRedirectUrl, isPlatformHostname, platformApiOrigin } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { StoreBaseProvider } from "@/lib/store";
import { CartProvider } from "@/lib/cart";
import { CartUIProvider } from "@/lib/cartui";
import { WishlistProvider } from "@/lib/wishlist";
import { CustomerAuthProvider } from "@/lib/auth";
import LocaleProvider from "@/i18n/LocaleProvider";
import StorefrontLayout from "./StorefrontLayout";
import StorefrontAbpMix from "./StorefrontAbpMix";
import SitePreloader from "@/components/SitePreloader";
import { normalizePreloader } from "@/lib/preloaders";
import { readStoreConfig, writeStoreConfig } from "@/lib/storeBoot";
import { isModernStorefront, isTrendyStorefront, isPerfectoStorefront, isBoxyStorefront, isMedicoStorefront, isMixyStorefront, isVariousStorefront, isBeastStorefront, isLocallyStorefront, isQuickstoStorefront, isBigshowStorefront, isFurnisStorefront, isCarPartsStorefront, isCosmosStorefront, isOndemandStorefront, resolveStoreColors, storefrontThemeId } from "@/lib/storefrontTheme";
import { OndemandPinGate } from "@/lib/ondemandPin";
import DeliverLogin from "../deliver/DeliverLogin";
import DeliverPanel from "../deliver/DeliverPanel";
import StorefrontCategory from "./StorefrontCategory";
import StorefrontCategories from "./StorefrontCategories";
import StorefrontShop from "./StorefrontShop";
import StorefrontWishlist from "./StorefrontWishlist";
import StorefrontProduct from "./StorefrontProduct";
import StorefrontCart from "./StorefrontCart";
import StorefrontCheckout from "./StorefrontCheckout";
import StorefrontOrder from "./StorefrontOrder";
import StorefrontPage from "./StorefrontPage";
import StorefrontCustomerLogin from "./StorefrontCustomerLogin";
import { StorefrontForgotPassword, StorefrontResetPassword } from "./StorefrontCustomerPassword";
import StorefrontCustomerSignup from "./StorefrontCustomerSignup";
import StorefrontMyOrders from "./StorefrontMyOrders";
import StorefrontAccount from "./StorefrontAccount";
import StorefrontUnderConstruction from "./StorefrontUnderConstruction";
import StorefrontAgeGate from "./StorefrontAgeGate";
import FortuneWheelWidget from "./FortuneWheelWidget";
import { OndemandLiveTrackFab } from "./OndemandLiveTrack";
import StorefrontBlogIndex from "./StorefrontBlogIndex";
import StorefrontBuildBox from "./StorefrontBuildBox";
import StorefrontBlogPost from "./StorefrontBlogPost";
import StorefrontCoursePage from "./StorefrontCoursePage";
import StorefrontAuthor from "./StorefrontAuthor";

const StorefrontHome = lazy(() => import("./StorefrontHome"));
const StorefrontHomeModern = lazy(() => import("./StorefrontHomeModern"));
const StorefrontHomeTrendy = lazy(() => import("./StorefrontHomeTrendy"));
const StorefrontHomePerfecto = lazy(() => import("./StorefrontHomePerfecto"));
const StorefrontHomeBoxy = lazy(() => import("./StorefrontHomeBoxy"));
const StorefrontHomeMedico = lazy(() => import("./StorefrontHomeMedico"));
const StorefrontHomeMixy = lazy(() => import("./StorefrontHomeMixy"));
const StorefrontHomeVarious = lazy(() => import("./StorefrontHomeVarious"));
const StorefrontHomeBeast = lazy(() => import("./StorefrontHomeBeast"));
const StorefrontHomeLocally = lazy(() => import("./StorefrontHomeLocally"));
const StorefrontHomeQuicksto = lazy(() => import("./StorefrontHomeQuicksto"));
const StorefrontHomeBigShow = lazy(() => import("./StorefrontHomeBigShow"));
const StorefrontHomeFurnis = lazy(() => import("./StorefrontHomeFurnis"));
const StorefrontHomeCarParts = lazy(() => import("./StorefrontHomeCarParts"));
const StorefrontHomeCosmos = lazy(() => import("./StorefrontHomeCosmos"));
const StorefrontHomeOndemand = lazy(() => import("./StorefrontHomeOndemand"));
const StorefrontSingleProduct = lazy(() => import("./StorefrontSingleProduct"));

function StoreHome({ config }) {
  if (config.single_product_active) return <StorefrontSingleProduct config={config} />;
  if (isOndemandStorefront(config)) return <StorefrontHomeOndemand config={config} />;
  if (isCosmosStorefront(config)) return <StorefrontHomeCosmos config={config} />;
  if (isCarPartsStorefront(config)) return <StorefrontHomeCarParts config={config} />;
  if (isFurnisStorefront(config)) return <StorefrontHomeFurnis config={config} />;
  if (isBigshowStorefront(config)) return <StorefrontHomeBigShow config={config} />;
  if (isQuickstoStorefront(config)) return <StorefrontHomeQuicksto config={config} />;
  if (isLocallyStorefront(config)) return <StorefrontHomeLocally config={config} />;
  if (isBeastStorefront(config)) return <StorefrontHomeBeast config={config} />;
  if (isVariousStorefront(config)) return <StorefrontHomeVarious config={config} />;
  if (isMixyStorefront(config)) return <StorefrontHomeMixy config={config} />;
  if (isMedicoStorefront(config)) return <StorefrontHomeMedico config={config} />;
  if (isBoxyStorefront(config)) return <StorefrontHomeBoxy config={config} />;
  if (isPerfectoStorefront(config)) return <StorefrontHomePerfecto config={config} />;
  if (isTrendyStorefront(config)) return <StorefrontHomeTrendy config={config} />;
  if (isModernStorefront(config)) return <StorefrontHomeModern config={config} />;
  return <StorefrontHome config={config} />;
}

export default function StorefrontShell({ forcedSlug, hintPreloader }) {
  const merchantAuth = useMerchantAuth();
  const params = useParams();
  const location = useLocation();
  const slug = forcedSlug || params.slug;
  // Custom-domain mode serves the store at the root -> clean links with no slug prefix.
  const base = forcedSlug ? "" : `/${slug}`;
  const [config, setConfig] = useState(() => readStoreConfig(slug));
  const [error, setError] = useState(null);
  const [geo, setGeo] = useState({ country: "IN", symbol: "₹", currency: "INR" });
  const [previewGate, setPreviewGate] = useState(() => {
    if (typeof window === "undefined") return false;
    if (new URLSearchParams(window.location.search).get("ms_preview")) return true;
    return Boolean(forcedSlug);
  });
  const seenSlug = useRef(slug);
  if (seenSlug.current !== slug) {
    seenSlug.current = slug;
    setConfig(readStoreConfig(slug));
    setError(null);
  }

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let live = true;

    const stripPreviewParam = () => {
      const qs = new URLSearchParams(window.location.search);
      if (!qs.has("ms_preview")) return;
      qs.delete("ms_preview");
      const rest = qs.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
    };

    const claimToken = async (token) => {
      await api.post("/auth/merchant/claim-preview", { token });
      if (typeof merchantAuth?.refresh === "function") await merchantAuth.refresh();
    };

    const run = async () => {
      const qs = new URLSearchParams(window.location.search);
      const qToken = qs.get("ms_preview");
      if (qToken) {
        try { await claimToken(qToken); } catch { /* expired */ }
        stripPreviewParam();
        return;
      }

      const onCustom = Boolean(forcedSlug) && !isPlatformHostname(window.location.hostname);
      if (!onCustom) return;

      const platform = platformApiOrigin();
      const bounceKey = "ms_preview_bounce_tried";
      try {
        const ref = document.referrer ? new URL(document.referrer) : null;
        const fromAdmin = Boolean(
          platform
          && ref
          && isPlatformHostname(ref.hostname)
          && ref.pathname.startsWith("/admin")
          && !sessionStorage.getItem(bounceKey)
        );
        if (fromAdmin) {
          sessionStorage.setItem(bounceKey, "1");
          const next = encodeURIComponent(`${window.location.origin}${window.location.pathname}${window.location.search}`);
          window.location.replace(`${platform}/api/auth/merchant/storefront-bounce?next=${next}`);
          return "navigating";
        }
      } catch { /* ignore */ }

      if (platform) {
        try {
          const { data } = await axios.post(
            `${platform}/api/auth/merchant/storefront-preview`,
            {},
            { withCredentials: true, timeout: 8000 },
          );
          if (data?.token) await claimToken(data.token);
        } catch { /* visitor or third-party cookies blocked */ }
      }
    };

    run().then((status) => {
      if (live && status !== "navigating") setPreviewGate(false);
    });
    return () => { live = false; };
  }, [forcedSlug]);

  useEffect(() => {
    if (previewGate) return undefined;
    const cached = readStoreConfig(slug);
    if (cached) setConfig(cached);
    else { setConfig(null); setError(null); }
    api.get(`/store/${slug}/config`)
      .then(({ data }) => {
        setConfig(data);
        setError(null);
        writeStoreConfig(slug, data);
      })
      .catch((e) => {
        const s = e?.response?.status;
        if (cached && s !== 404 && s !== 410) return;
        setConfig(null);
        setError(s === 404 ? "not-found" : s === 410 ? "expired" : "error");
      });
    api.get(`/store/${slug}/geo`).then(({ data }) => setGeo(data)).catch(() => {});
  }, [slug, previewGate]);

  useLayoutEffect(() => {
    if (!config) return;
    const seoTitle = (config.seo?.meta_title || "").trim();
    const name = (config.store_info?.name || "").trim();
    const next = seoTitle || (name ? `${name} — Online Store` : "");
    if (next) document.title = next;
  }, [config]);

  useLayoutEffect(() => {
    if (!config) return undefined;
    const themeId = storefrontThemeId(config);
    const accent = (config.branding?.accent_color || "").trim() || (themeId === "mixy" ? "#FFDC2E" : "");
    if (accent) document.documentElement.style.setProperty("--mixy-accent", accent);
    return () => document.documentElement.style.removeProperty("--mixy-accent");
  }, [config]);

  const preloaderId = normalizePreloader(config?.store_info?.preloader || hintPreloader);
  const storeLoading = (
    <div className="overflow-hidden" data-testid="store-loading">
      <SitePreloader id={preloaderId} />
    </div>
  );

  if (previewGate) return storeLoading;
  if (error === "not-found") return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 text-center p-8">
      <div><div className="text-5xl mb-4">🏪</div><h1 className="font-display text-2xl font-bold text-stone-900">Store not found</h1><p className="text-stone-500 mt-2">The store <code className="font-mono-nums">yoursaas.com/{slug}</code> does not exist.</p></div>
    </div>
  );
  if (error === "expired") return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 text-center p-8" data-testid="store-expired">
      <div className="max-w-md">
        <div className="text-5xl mb-4">🔒</div>
        <h1 className="font-display text-2xl font-bold text-stone-900">Store temporarily unavailable</h1>
        <p className="text-stone-500 mt-2">This store is currently paused. Please check back soon.</p>
      </div>
    </div>
  );
  if (!config) return storeLoading;

  const pathAfterBase = forcedSlug
    ? location.pathname
    : location.pathname.replace(new RegExp(`^/${slug}`), "") || "/";
  const onDeliver = pathAfterBase === "/deliver" || pathAfterBase.startsWith("/deliver/");
  if (onDeliver) {
    return (
      <StoreBaseProvider value={{ slug, base, country: geo.country, currency: geo.currency, symbol: geo.symbol, storeConfig: config }}>
        <Routes>
          <Route path="deliver" element={<DeliverLogin />} />
          <Route path="deliver/orders" element={<DeliverPanel />} />
        </Routes>
      </StoreBaseProvider>
    );
  }

  const merchantOwnsStore = Boolean(
    config.viewer_is_merchant
    || (merchantAuth?.tenant?.id && config.tenant?.id && merchantAuth.tenant.id === config.tenant.id)
    || (merchantAuth?.tenant?.slug && merchantAuth.tenant.slug === slug)
  );

  const customDomainTarget = !forcedSlug
    ? slugStorefrontRedirectUrl(config.tenant, {
        slug,
        pathname: typeof window !== "undefined" ? window.location.pathname : `/${slug}`,
        search: typeof window !== "undefined" ? window.location.search : "",
        hash: typeof window !== "undefined" ? window.location.hash : "",
        hostname: typeof window !== "undefined" ? window.location.hostname : "",
      })
    : null;
  if (customDomainTarget) {
    if (typeof window !== "undefined") window.location.replace(customDomainTarget);
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500" data-testid="store-domain-redirect">
        Redirecting to {config.tenant?.custom_domain}…
      </div>
    );
  }
  if (config.under_construction?.enabled) {
    if (merchantAuth?.loading && !merchantOwnsStore) {
      return storeLoading;
    }
    if (!merchantOwnsStore) {
      const ucColors = resolveStoreColors(config);
      const cssVarsUc = {
        "--store-primary": ucColors.primary,
        "--store-secondary": ucColors.secondary,
      };
      return (
        <div style={cssVarsUc} className="overflow-x-clip">
          <StorefrontUnderConstruction config={config} />
        </div>
      );
    }
  }

  const themeId = storefrontThemeId(config);
  const brand = resolveStoreColors(config);
  const pageBg = /^#[0-9A-Fa-f]{6}$/.test(String(config.store_info?.page_bg_color || "").trim())
    ? String(config.store_info.page_bg_color).trim()
    : "";
  const cssVars = {
    "--store-primary": brand.primary,
    "--store-secondary": brand.secondary,
    "--mixy-accent": (config.branding?.accent_color || "").trim() || (themeId === "mixy" ? "#FFDC2E" : brand.secondary),
    ...(pageBg ? { "--store-page-bg": pageBg } : {}),
  };

  const storeLng = config.i18n?.google_storefront ? "en" : (config.i18n?.storefront_language || "en");

  return (
    <LocaleProvider lng={storeLng}>
    <div style={{ ...cssVars, ...(pageBg ? { backgroundColor: pageBg } : {}) }} className={`min-h-screen overflow-x-clip fade-in ${pageBg ? "sf-page-bg" : storefrontThemeId(config) === "classic" ? "bg-stone-50" : storefrontThemeId(config) === "beast" ? "bg-black" : "bg-white"}`}>
      {pageBg ? (
        <style>{`.sf-page-bg main{background-color:var(--store-page-bg)}.sf-page-bg main>.bg-white,.sf-page-bg main>.bg-stone-50,.sf-page-bg main>.bg-black,.sf-page-bg main>.bg-neutral-50,.sf-page-bg main>.bg-neutral-950,.sf-page-bg main>[class*="bg-white"],.sf-page-bg main>[class*="bg-stone-50"],.sf-page-bg main>[class*="bg-black"]{background-color:var(--store-page-bg)!important}`}</style>
      ) : null}
      <StoreBaseProvider value={{ slug, base, country: geo.country, currency: geo.currency, symbol: geo.symbol, storeConfig: config }}>
      <CartProvider slug={slug}>
        <WishlistProvider slug={slug}>
          <CartUIProvider skipCart={Boolean(config.preferences?.skip_cart)}>
            <CustomerAuthProvider slug={slug}>
              <StorefrontLayout config={{ ...config, viewer_is_merchant: merchantOwnsStore }}>
                <OndemandPinGate slug={slug} gate={config.ondemand_gate} />
                <StorefrontAgeGate config={config} />
                <FortuneWheelWidget config={config} />
                <OndemandLiveTrackFab slug={slug} base={base} config={config} />
                <Routes>
                  <Route index element={<Suspense fallback={<SitePreloader id={preloaderId} testId="store-theme-loading" compact />}><StoreHome config={config} /></Suspense>} />
                  <Route path="shop" element={<StorefrontShop config={config} />} />
                  <Route path="categories" element={<StorefrontCategories config={config} />} />
                  <Route path="category/:catSlug" element={<StorefrontCategory config={config} />} />
                  <Route path="product/:pSlug" element={<StorefrontProduct config={config} />} />
                  <Route path="wishlist" element={<StorefrontWishlist config={config} />} />
                  <Route path="cart" element={<StorefrontCart config={config} />} />
                  <Route path="checkout" element={<StorefrontCheckout config={config} />} />
                  <Route path="order/:orderId" element={<StorefrontOrder config={config} />} />
                  <Route path="page/:pageSlug" element={<StorefrontPage config={config} />} />
                  <Route path="blog" element={<StorefrontBlogIndex config={config} />} />
                  <Route path="blog/:postSlug" element={<StorefrontBlogPost config={config} />} />
                  <Route path="course/:courseSlug" element={<StorefrontCoursePage config={config} />} />
                  <Route path="author/:authorSlug" element={<StorefrontAuthor config={config} />} />
                  <Route path="collections/:boxSlug" element={<StorefrontBuildBox />} />
                  <Route path="mix/:mixSlug" element={<StorefrontAbpMix />} />
                  <Route path="account/login" element={<StorefrontCustomerLogin config={config} />} />
                  <Route path="account/forgot-password" element={<StorefrontForgotPassword config={config} />} />
                  <Route path="account/reset-password" element={<StorefrontResetPassword />} />
                  <Route path="account/signup" element={<StorefrontCustomerSignup config={config} />} />
                  <Route path="account" element={<StorefrontAccount config={config} />} />
                  <Route path="account/orders" element={<StorefrontMyOrders config={config} />} />
                  <Route path="*" element={<Navigate to="" replace />} />
                </Routes>
              </StorefrontLayout>
            </CustomerAuthProvider>
          </CartUIProvider>
        </WishlistProvider>
      </CartProvider>
      </StoreBaseProvider>
    </div>
    </LocaleProvider>
  );
}
